// ── CATÁLOGO CANÓNICO DE TIPOS DE SERVICIO ───────────────────────────────────
//
// FUENTE ÚNICA DE VERDAD del valor que se PERSISTE en `maintenanceRecords.serviceType`.
// `value` es el dato guardado (NO se traduce, va tal cual a Firestore); `labelKey`
// es el sufijo de la clave i18n que cada pantalla antepone con su namespace.
//
// ⚠️ COPIA A MANO en `functions/src/maintenance.js` (SERVICE_TYPES + normalizeServiceType).
// Las Cloud Functions no pueden importar de `src/`, así que la duplicación es
// deliberada — mismo caso que `ASSISTANT_TOOLS` (ai.js ↔ src/assistant/tools.js) y
// `clientPro.js`. Si se cambia una copia hay que cambiar la otra; la guarda
// automática está en `functions/tests/serviceTypeCatalog.test.js`, que extrae este
// catálogo del fuente y lo compara con el del backend.
//
// Por qué existe: el tipo de servicio se escribía con TRES vocabularios que no se
// cruzaban (cliente con tildes, mecánico sin tildes, cron con otra caja) y las
// alertas se resuelven comparando texto exacto. Resultado: casi nada de lo que
// registraba un mecánico generaba alerta. Por eso `canonicalServiceType()` se usa
// en TODA comparación: los meses de registros históricos con las tres formas
// siguen resolviendo a la misma regla sin tocar un solo documento de Firestore.
//
// `selectable: false` = tipo que ningún formulario ofrece; existe solo porque el
// cron de correos (`functions/src/maintenance.js`) lo busca en el historial.
export const SERVICE_TYPES = [
  { value: 'Cambio de Aceite',       labelKey: 'oilChange',    selectable: true },
  { value: 'Frenos',                 labelKey: 'brakes',       selectable: true },
  { value: 'Neumáticos',             labelKey: 'tires',        selectable: true },
  { value: 'Batería',                labelKey: 'battery',      selectable: true },
  { value: 'Inspección',             labelKey: 'inspection',   selectable: true },
  { value: 'Transmisión',            labelKey: 'transmission', selectable: true },
  { value: 'Suspensión',             labelKey: 'suspension',   selectable: true },
  { value: 'Reparación',             labelKey: 'repair',       selectable: true },
  { value: 'Otro',                   labelKey: 'other',        selectable: true },
  { value: 'Filtro de aire',         labelKey: 'airFilter',    selectable: false },
  { value: 'Filtro de combustible',  labelKey: 'fuelFilter',   selectable: false },
  { value: 'Bujías',                 labelKey: 'sparkPlugs',   selectable: false },
  { value: 'Correa de distribución', labelKey: 'timingBelt',   selectable: false },
];

// Los que se ofrecen en formularios. El orden es el de presentación.
export const SERVICE_TYPE_OPTIONS = SERVICE_TYPES.filter(s => s.selectable);

// Clave de comparación: sin tildes, sin distinguir mayúsculas, sin espacios de más.
// Es lo que permite que 'Neumaticos', 'Neumáticos' y 'NEUMATICOS' sean el mismo
// servicio sin migrar los datos ya guardados.
export function normalizeServiceType(value) {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

const CANONICAL_BY_KEY = SERVICE_TYPES.reduce((acc, s) => {
  acc[normalizeServiceType(s.value)] = s.value;
  return acc;
}, {});

// Devuelve el valor canónico de cualquier forma histórica, o null si el tipo no
// está en el catálogo (texto libre del asistente de voz, servicios sueltos, etc.).
export function canonicalServiceType(value) {
  return CANONICAL_BY_KEY[normalizeServiceType(value)] || null;
}

// ── INTERVALOS POR DEFECTO ───────────────────────────────────────────────────
//
// ⚠️ ESTOS NÚMEROS SON UNA DECISIÓN DE PRODUCTO, REVISABLE. NO son una verdad
// de ingeniería ni salen de una especificación del fabricante. Se eligieron
// pensando en República Dominicana (aceite convencional, calor, tráfico) y
// tomando el valor MÁS CONSERVADOR de los dos que el código tenía divergiendo.
// Cambiar un número aquí cambia el default de toda la app: es una línea.
//
// FUENTE ÚNICA DE VERDAD de los intervalos. Antes vivían en dos tablas con
// valores distintos (`functions/src/maintenance.js` decía aceite 5.000 km y
// frenos 40.000; esta decía aceite 8.000 y frenos 20.000) y nada obligaba a que
// coincidieran. La clave es el VALOR CANÓNICO de `SERVICE_TYPES`, el mismo que
// se persiste en `maintenanceRecords.serviceType`.
//
// ⚠️ COPIA A MANO en `functions/src/maintenance.js` (mismo caso que
// SERVICE_TYPES: las Cloud Functions no pueden importar de `src/`).
//
// `km: null`  = ese servicio no se mide por kilometraje (solo por tiempo).
// `months: null` = ese servicio no se mide por tiempo (solo por kilometraje).
export const DEFAULT_INTERVALS = {
  'Cambio de Aceite': { km: 5000,  months: 4 },
  'Frenos':           { km: 20000, months: 24 },
  'Neumáticos':       { km: 40000, months: 36 },
  'Batería':          { km: null,  months: 36 },
  'Inspección':       { km: 10000, months: 12 },
  'Transmisión':      { km: 60000, months: 48 },
  'Suspensión':       { km: 80000, months: 60 },
};

// Motocicletas / pasolas / scooters (vehicleType === 'moto'): mismos tipos de
// servicio, intervalos mucho más cortos (motor pequeño, mayor revolución).
// 'Transmisión' cubre la cadena.
export const MOTO_DEFAULT_INTERVALS = {
  'Cambio de Aceite': { km: 2500,  months: 3 },
  'Frenos':           { km: 12000, months: 18 },
  'Neumáticos':       { km: 20000, months: 36 },
  'Batería':          { km: null,  months: 24 },
  'Inspección':       { km: 5000,  months: 12 },
  'Transmisión':      { km: 12000, months: 24 },
  'Suspensión':       { km: 30000, months: 48 },
};

// Umbrales de aviso (cuándo poner la alerta en amarillo) y la equivalencia en
// millas que muestran los formularios. Los INTERVALOS no se repiten aquí: se
// toman de las tablas de arriba, para que no puedan volver a divergir.
const AUTO_WARNINGS = {
  'Cambio de Aceite': { milesInterval: 3100,  warningKm: 800,  warningMonths: 1, warningDays: 15 },
  'Frenos':           { milesInterval: 12500, warningKm: 2000, warningMonths: 2 },
  'Neumáticos':       { milesInterval: 25000, warningKm: 5000, warningMonths: 3 },
  'Batería':          { milesInterval: null,  warningKm: null, warningMonths: 6 },
  'Inspección':       { milesInterval: 6000,  warningKm: 1000, warningMonths: 1 },
  'Transmisión':      { milesInterval: 37500, warningKm: 5000, warningMonths: 6 },
  'Suspensión':       { milesInterval: 50000, warningKm: 5000, warningMonths: 6 },
};

const MOTO_WARNINGS = {
  'Cambio de Aceite': { milesInterval: 1500,  warningKm: 300,  warningMonths: 1, warningDays: 10 },
  'Frenos':           { milesInterval: 7500,  warningKm: 1500, warningMonths: 2 },
  'Neumáticos':       { milesInterval: 12500, warningKm: 2500, warningMonths: 3 },
  'Batería':          { milesInterval: null,  warningKm: null, warningMonths: 4 },
  'Inspección':       { milesInterval: 3000,  warningKm: 500,  warningMonths: 1 },
  'Transmisión':      { milesInterval: 7500,  warningKm: 1500, warningMonths: 3 },
  'Suspensión':       { milesInterval: 18750, warningKm: 3000, warningMonths: 6 },
};

function buildRules(intervals, warnings) {
  const out = {};
  for (const [type, iv] of Object.entries(intervals)) {
    out[type] = {
      kmInterval: iv.km,
      monthsInterval: iv.months,
      ...(warnings[type] || {}),
    };
  }
  return out;
}

export const maintenanceRules = buildRules(DEFAULT_INTERVALS, AUTO_WARNINGS);
export const motoMaintenanceRules = buildRules(MOTO_DEFAULT_INTERVALS, MOTO_WARNINGS);

// Devuelve el set de reglas correcto según el tipo de vehículo.
export function getRulesForVehicle(vehicle) {
  return (vehicle?.vehicleType === 'moto') ? motoMaintenanceRules : maintenanceRules;
}

// Devuelve la tabla de intervalos por defecto que le toca a este vehículo.
export function defaultIntervalsFor(vehicle) {
  return (vehicle?.vehicleType === 'moto') ? MOTO_DEFAULT_INTERVALS : DEFAULT_INTERVALS;
}

const numOrNull = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};

// ── LÍMITES DE UN INTERVALO CONFIGURABLE ─────────────────────────────────────
//
// Por qué existen: desde que el cron manda correos de mantenimiento, un
// intervalo absurdo ("cada 1 km") deja de ser un número feo en pantalla y se
// convierte en spam al cliente. Y un campo con basura ("abc") era PEOR: el
// código lo convertía en silencio a `null`, que en el contrato significa "este
// servicio no se mide por ese eje", o sea APAGABA la alerta sin avisar.
//
// Los números:
//  · km 500–200.000. Abajo: el intervalo permanente más corto que publica un
//    fabricante real es ~1.000 km (aceite mineral en una pasola de 125cc en
//    servicio severo); 500 deja margen para un dueño más estricto sin dejar
//    pasar 1, 10 ni 50 — y de paso atrapa el clásico "5.000" pegado, que
//    `Number()` lee como 5. Arriba: 200.000 cubre de sobra lo más largo que se
//    publica (transmisión / correa ~100.000–150.000 km); por encima la alerta
//    no saltaría nunca en la vida del vehículo, y para decir eso ya existe el
//    campo vacío (null).
//  · meses 1–120. Un mes es el recordatorio por tiempo más corto que tiene
//    sentido; 120 meses (10 años) queda por encima de todo default nuestro
//    (máx. 60) y de cualquier recomendación real por tiempo. Más allá → null.
//  · Enteros en los dos ejes: los meses entran en `setMonth()` y los km se
//    muestran con `toLocaleString`; un decimal es un error de tecleo, no una
//    intención.
//  · `null` (campo vacío) SIGUE siendo válido: es la forma explícita de decir
//    "este eje no se mide", y `resolveInterval` ya la respeta.
export const INTERVAL_LIMITS = {
  km:     { min: 500, max: 200000 },
  months: { min: 1,   max: 120 },
};

/**
 * Normaliza UN valor de intervalo tecleado por el usuario.
 * @returns {{ok:true, value:number|null} | {ok:false, reason:'number'|'range'}}
 */
export function normalizeIntervalValue(axis, raw) {
  if (raw === null || raw === undefined || String(raw).trim() === '') {
    return { ok: true, value: null };   // "este eje no se mide" — decisión válida
  }
  const n = Number(raw);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return { ok: false, reason: 'number' };
  const lim = INTERVAL_LIMITS[axis];
  if (lim && (n < lim.min || n > lim.max)) return { ok: false, reason: 'range' };
  return { ok: true, value: n };
}

// Traduce un fallo de validación a la clave i18n que lo explica. Vive aquí para
// que las tres vistas digan lo mismo; el componente hace el `t(key, params)`.
export function intervalErrorMessage(invalid) {
  const { type, axis, reason } = invalid || {};
  if (reason === 'range') {
    const lim = INTERVAL_LIMITS[axis] || {};
    return {
      key: axis === 'months' ? 'vehicleIntervals.rangeMonths' : 'vehicleIntervals.rangeKm',
      params: { service: type, min: lim.min, max: lim.max },
    };
  }
  return { key: 'vehicleIntervals.invalidNumber', params: { service: type } };
}

/**
 * FUENTE ÚNICA de la regla de oro de los intervalos por vehículo.
 *
 * ⚠️ REGLA DE ORO: el eje que quedó IGUAL al default NO se escribe, y el
 * servicio cuyos dos ejes son el default se OMITE por completo. Ausente
 * significa "usa el default", así el carro se beneficia si algún día mejoramos
 * la tabla; escribir el número lo congelaría para siempre.
 *
 * Antes esto vivía copiado tres veces (`buildIntervalsPatch` inline en
 * VehicleDetail y en VehicleDetailDesktop, `buildIntervalPatch` en
 * MechanicAddService). Aquí es una sola.
 *
 * Semántica de FUSIÓN, no de reemplazo: solo se tocan los tipos y los ejes que
 * vengan en `formValues`. Eso es lo que permite que el formulario completo de
 * la ficha y el de un solo servicio del mecánico usen la misma función: un tipo
 * ausente conserva lo que tuviera, en vez de leerse como "vacío" (= null =
 * alerta apagada).
 *
 * @param {object|null} vehicle    doc de `vehicles`; de él salen la tabla de
 *                                 defaults (auto/moto) y lo ya configurado.
 * @param {object} formValues      { [tipoDeServicio]: { km?, months? } } con
 *                                 los valores tal como los tecleó el usuario.
 * @returns {{ok:boolean, changed:boolean, intervals:object|null,
 *            invalid:Array<{type:string, axis:'km'|'months', value:*,
 *                           reason:'number'|'range'}>}}
 *          `ok:false` ⇒ `intervals:null`: no se persiste NADA, ni a medias.
 */
export function diffIntervals(vehicle, formValues) {
  const defaults = defaultIntervalsFor(vehicle);
  const base = vehicle?.maintenanceIntervals || {};
  const next = { ...base };
  const invalid = [];

  for (const [rawType, row] of Object.entries(formValues || {})) {
    const canonical = canonicalServiceType(rawType);
    const def = canonical ? defaults[canonical] : null;
    // Tipo fuera del catálogo de intervalos ('Reparación', 'Otro', texto libre):
    // no tiene default contra el cual comparar, así que no se configura.
    if (!def) continue;

    const entry = { ...(base[canonical] || {}) };
    for (const axis of ['km', 'months']) {
      if (!row || !(axis in row)) continue;   // eje que el formulario no trae
      const parsed = normalizeIntervalValue(axis, row[axis]);
      if (!parsed.ok) {
        invalid.push({ type: canonical, axis, value: row[axis], reason: parsed.reason });
        continue;
      }
      if (parsed.value === (def[axis] ?? null)) delete entry[axis];
      else entry[axis] = parsed.value;
    }

    if (Object.keys(entry).length > 0) next[canonical] = entry;
    else delete next[canonical];
  }

  if (invalid.length) return { ok: false, changed: false, intervals: null, invalid };
  return {
    ok: true,
    changed: JSON.stringify(next) !== JSON.stringify(base),
    intervals: next,
    invalid,
  };
}

// Aplica los niveles 2 y 3 sobre un intervalo base ya resuelto (nivel 1).
// Se separa de `resolveInterval` porque `calculateNextMaintenance` recibe la
// tabla de reglas por parámetro (los llamadores viejos pasan las de moto sin
// pasar el vehículo) y ese nivel 1 debe seguir mandando.
function applyIntervalLevels(base, vehicle, canonical, record) {
  if (!base) return null;
  const out = {
    km: base.km ?? null,
    months: base.months ?? null,
    source: { km: 'default', months: 'default' },
  };

  // Nivel 2 — lo que el dueño (o el mecánico) configuró para ESE carro.
  // Se distingue "la clave no viene" de "la clave viene en null": null es una
  // decisión explícita ("este servicio no se mide por ese eje").
  const cfg = vehicle?.maintenanceIntervals?.[canonical];
  if (cfg && typeof cfg === 'object') {
    if ('km' in cfg) { out.km = numOrNull(cfg.km); out.source.km = 'vehicle'; }
    if ('months' in cfg) { out.months = numOrNull(cfg.months); out.source.months = 'vehicle'; }
  }

  // LEGACY — `oilChangeIntervalMonths` por registro. Está retirado del camino
  // nuevo (el intervalo se configura por vehículo), pero se sigue leyendo para
  // que los carros que ya lo tienen NO cambien de comportamiento. La
  // configuración del vehículo, si existe, le gana.
  if (out.source.months === 'default' && record?.oilChangeIntervalMonths) {
    const legacy = numOrNull(record.oilChangeIntervalMonths);
    if (legacy) { out.months = legacy; out.source.months = 'record'; }
  }

  // Nivel 3 — `customNextKm`: el mecánico dice "esta vez vuelve antes".
  // Es el más específico de todos y solo toca el eje de kilometraje.
  if (record?.customNextKm) {
    const custom = numOrNull(record.customNextKm);
    if (custom) { out.km = custom; out.source.km = 'record'; }
  }

  return out;
}

/**
 * Intervalo efectivo de un servicio para un vehículo, resolviendo los tres
 * niveles (gana el más específico):
 *
 *   1. DEFAULT_INTERVALS[tipo]  (o MOTO_DEFAULT_INTERVALS si es moto)
 *   2. vehicle.maintenanceIntervals[tipo]
 *   3. record.customNextKm  (solo km) · record.oilChangeIntervalMonths (legacy)
 *
 * @param {object|null} vehicle      doc de `vehicles` (puede ser null)
 * @param {string} serviceType       cualquier forma histórica; se canoniza
 * @param {object|null} [record]     doc de `maintenanceRecords` (opcional)
 * @returns {{km:number|null, months:number|null,
 *            source:{km:'default'|'vehicle'|'record',
 *                    months:'default'|'vehicle'|'record'}}|null}
 *          null si el tipo no tiene intervalo definido (p. ej. 'Reparación').
 */
export function resolveInterval(vehicle, serviceType, record = null) {
  const canonical = canonicalServiceType(serviceType);
  if (!canonical) return null;
  return applyIntervalLevels(defaultIntervalsFor(vehicle)[canonical], vehicle, canonical, record);
}

// `vehicle` es OPCIONAL a propósito: los llamadores viejos solo pasan la tabla
// de reglas. Sin vehículo se resuelven los niveles 1 y 3 (exactamente el
// comportamiento anterior); con vehículo entra además el nivel 2.
export function calculateNextMaintenance(lastService, currentMileage, rules = maintenanceRules, vehicle = null) {
  // El tipo guardado puede venir en cualquiera de las formas históricas
  // ('Neumaticos', 'Neumáticos', 'NEUMATICOS'): se resuelve al canónico ANTES de
  // buscar la regla, si no el registro del mecánico nunca encuentra su intervalo.
  const canonical = canonicalServiceType(lastService.serviceType);
  const rule = canonical ? rules[canonical] : undefined;
  if (!rule) return null;

  // Nivel 1 = la tabla que se recibió por parámetro (auto o moto).
  const interval = applyIntervalLevels(
    { km: rule.kmInterval, months: rule.monthsInterval },
    vehicle, canonical, lastService
  );

  const result = {
    // Se devuelve el valor canónico, no el guardado: los mensajes y los títulos
    // de notificación que se arman con esto quedan iguales por las dos vías
    // (Home y cron), que es lo que permite deduplicarlos entre sí.
    serviceType: canonical,
    status: 'ok',
    message: '',
    kmRemaining: null,
    monthsRemaining: null
  };

  const kmInterval = interval.km;

  if (kmInterval) {
    const nextKm = lastService.mileageAtService + kmInterval;
    const kmRemaining = nextKm - currentMileage;
    result.kmRemaining = kmRemaining;
    result.kmInterval = kmInterval;
    result.nextKm = nextKm;

    if (kmRemaining <= 0) {
      result.status = 'overdue';
      const absKm = Math.abs(kmRemaining);
      result.message = `${canonical} vencido hace ${absKm.toLocaleString()} km (${Math.round(absKm / 1.60934).toLocaleString()} mi)`;
    } else if (kmRemaining <= (rule.warningKm || Math.round(kmInterval * 0.1))) {
      result.status = 'warning';
      result.message = `${canonical} vence en ${kmRemaining.toLocaleString()} km (${Math.round(kmRemaining / 1.60934).toLocaleString()} mi)`;
    } else {
      result.status = 'ok';
      result.message = `${canonical} faltan ${kmRemaining.toLocaleString()} km (${Math.round(kmRemaining / 1.60934).toLocaleString()} mi)`;
    }
  }

  // Eje de TIEMPO. La alerta salta con lo que llegue primero, km o meses: el
  // bloque de km de arriba ya pudo dejar `overdue`, y este solo puede empeorar
  // el estado, nunca mejorarlo.
  const intervalMonths = interval.months;
  const warningDays = rule.warningDays || (rule.warningMonths ? rule.warningMonths * 30 : 30);

  // `months: null` puesto a propósito para ESE carro = ese servicio se mide
  // solo por kilometraje. Se ignora incluso un alertDate ya guardado, o el
  // dueño no podría apagar el aviso por tiempo.
  const monthsDisabled = interval.months === null && interval.source.months === 'vehicle';

  const rawAlertDate = lastService.alertDate?.toDate?.() || lastService.alertDate;
  let effectiveAlertDate = (!monthsDisabled && rawAlertDate instanceof Date) ? rawAlertDate : null;

  if (!effectiveAlertDate && !monthsDisabled && intervalMonths && lastService.serviceDate) {
    // Calcular alertDate desde serviceDate + intervalo en meses
    const sd = lastService.serviceDate.toDate ? lastService.serviceDate.toDate() : lastService.serviceDate;
    if (sd instanceof Date) {
      effectiveAlertDate = new Date(sd);
      effectiveAlertDate.setMonth(effectiveAlertDate.getMonth() + intervalMonths);
    }
  }

  if (effectiveAlertDate) {
    const today = new Date();
    const daysRemaining = Math.floor((effectiveAlertDate - today) / (1000 * 60 * 60 * 24));
    result.monthsRemaining = Math.floor(daysRemaining / 30);

    if (daysRemaining <= 0) {
      result.status = 'overdue';
      const absDays = Math.abs(daysRemaining);
      if (result.status !== 'overdue' || !result.message) {
        result.message = `${canonical} vencido hace ${absDays} día${absDays !== 1 ? 's' : ''}`;
      }
      // km overdue takes priority if set; otherwise use date message
      if (!result.kmRemaining || result.kmRemaining > 0) {
        result.message = `${canonical} vencido hace ${absDays} día${absDays !== 1 ? 's' : ''}`;
      }
    } else if (daysRemaining <= warningDays && result.status !== 'overdue') {
      result.status = 'warning';
      result.message = `${canonical} vence en ${daysRemaining} día${daysRemaining !== 1 ? 's' : ''} (${effectiveAlertDate.toLocaleDateString('es-DO')})`;
    }
  }

  return result;
}

export function getVehicleAlerts(vehicle, maintenanceRecords) {
  const alerts = [];
  const serviceTypes = {};
  const rules = getRulesForVehicle(vehicle);

  maintenanceRecords.forEach(record => {
    // Se agrupa por el tipo CANÓNICO: si no, un carro con un 'Neumaticos' del
    // mecánico y un 'Neumáticos' del dueño se trata como dos servicios distintos
    // y puede ganar el registro más viejo. La forma normalizada se usa como clave
    // de respaldo para que los tipos fuera del catálogo no se mezclen entre sí.
    const key = canonicalServiceType(record.serviceType) || normalizeServiceType(record.serviceType);
    if (!serviceTypes[key] ||
        record.serviceDate?.toDate() > serviceTypes[key].serviceDate?.toDate()) {
      serviceTypes[key] = record;
    }
  });

  Object.values(serviceTypes).forEach(lastService => {
    // Se pasa el vehículo para que entre el nivel 2 (`maintenanceIntervals`).
    const next = calculateNextMaintenance(lastService, vehicle.currentMileage, rules, vehicle);
    if (next && (next.status === 'warning' || next.status === 'overdue')) {
      alerts.push(next);
    }
  });

  return alerts.sort((a, b) => {
    if (a.status === 'overdue' && b.status !== 'overdue') return -1;
    if (a.status !== 'overdue' && b.status === 'overdue') return 1;
    return 0;
  });
}

// ── Salud del vehículo (score 0–100) y recomendaciones ──────────────────────

// Penalización de salud por alerta, sensible a severidad.
// Un servicio vencido pesa más mientras más vencido esté (por km o por tiempo).
export function getAlertPenalty(alert) {
  if (!alert) return 0;
  if (alert.status === 'overdue') {
    const kmOver = (alert.kmRemaining != null && alert.kmRemaining < 0) ? Math.abs(alert.kmRemaining) : 0;
    const monthsOver = (alert.monthsRemaining != null && alert.monthsRemaining < 0) ? Math.abs(alert.monthsRemaining) : 0;
    const extra = Math.max(Math.min(20, kmOver / 250), Math.min(20, monthsOver * 7));
    return Math.min(52, 32 + extra);
  }
  // warning (próximo): penalización que sube mientras más cerca del vencimiento.
  let prox = 0;
  if (alert.kmRemaining != null && alert.kmRemaining > 0) {
    prox = Math.min(6, (800 - Math.min(800, alert.kmRemaining)) / 130);
  }
  return Math.min(14, 8 + prox);
}

export function getVehicleScore(alerts) {
  let score = 100;
  (alerts || []).forEach(a => { score -= getAlertPenalty(a); });
  return Math.max(5, Math.min(100, Math.round(score)));
}

// Salud de la flota: promedio inclinado hacia el peor vehículo, para que un
// problema serio en un carro no quede diluido por los carros sanos.
export function getFleetHealth(alertsPerVehicle) {
  const scores = (alertsPerVehicle || []).map(getVehicleScore);
  if (!scores.length) return 100;
  const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
  const min = Math.min(...scores);
  return Math.round(0.6 * min + 0.4 * avg);
}

// Ciclo nacional del marbete (derecho a circulación) en República Dominicana.
// La DGII abre la renovación ~21 de octubre y vence el 31 de enero (sin recargo);
// recargo del 1 al 28 de febrero. Se repite cada año. Devuelve null fuera de temporada.
export function getMarbeteStatus(now = new Date()) {
  const y = now.getFullYear();
  const m = now.getMonth(); // 0 = enero
  const deadlineYear = m >= 9 ? y + 1 : y; // oct–dic apuntan al 31 ene del año siguiente
  const deadline = new Date(deadlineYear, 0, 31, 23, 59, 59);       // 31 enero
  const windowOpen = new Date(deadlineYear - 1, 9, 21, 0, 0, 0);    // 21 octubre
  const surchargeEnd = new Date(deadlineYear, 1, 28, 23, 59, 59);   // 28 febrero
  if (now < windowOpen || now > surchargeEnd) return null;          // fuera de temporada
  const msDay = 86400000;
  if (now <= deadline) {
    return { status: 'upcoming', daysLeft: Math.ceil((deadline - now) / msDay), deadline };
  }
  return { status: 'overdue', daysOver: Math.ceil((now - deadline) / msDay), deadline };
}

// Recomendaciones accionables por vehículo. Devuelve datos estructurados;
// el componente arma los textos i18n según el `type`.
export function getVehicleRecommendations(vehicle, alerts, records, fuelLogs) {
  const recs = [];
  const vehicleName = `${vehicle.brand} ${vehicle.model}${vehicle.year ? ' ' + vehicle.year : ''}`;
  const base = { vehicleId: vehicle.id, vehicleName };

  // 1. Mantenimiento (desde las alertas detectadas).
  // `impact` = puntos de salud que recuperas al resolverlo (la propia penalización).
  (alerts || []).forEach(a => {
    recs.push({
      ...base,
      priority: a.status === 'overdue' ? 'high' : 'medium',
      type: 'maintenance',
      serviceType: a.serviceType,
      status: a.status,
      message: a.message,
      impact: Math.round(getAlertPenalty(a)),
    });
  });

  // 2. Sin registro de cambio de aceite: no podemos cuidar el motor
  const hasOil = (records || []).some(r => canonicalServiceType(r.serviceType) === 'Cambio de Aceite');
  if (!hasOil) {
    recs.push({ ...base, priority: 'low', type: 'baseline' });
  }

  // 3. Consumo de combustible cayendo (fuelLogs ordenados por fecha desc)
  const eff = (fuelLogs || []).map(f => Number(f.efficiency)).filter(n => n > 0);
  if (eff.length >= 4) {
    const recent = eff.slice(0, 2);
    const older = eff.slice(2);
    const recentAvg = recent.reduce((a, b) => a + b, 0) / recent.length;
    const olderAvg = older.reduce((a, b) => a + b, 0) / older.length;
    if (olderAvg > 0 && recentAvg < olderAvg * 0.85) {
      recs.push({ ...base, priority: 'medium', type: 'fuel', from: olderAvg, to: recentAvg });
    }
  }

  // 4. Marbete (ciclo nacional RD). Se omite si ya hay un marbete registrado
  // en los documentos del vehículo que vence después del deadline (ya renovado).
  const marbete = getMarbeteStatus();
  if (marbete) {
    const rawDoc = vehicle.documents?.marbete?.expiresAt || vehicle.documents?.marbete;
    let renewed = false;
    if (rawDoc) {
      const d = rawDoc.toDate?.() || new Date(rawDoc);
      if (d instanceof Date && !isNaN(d.getTime()) && d > marbete.deadline) renewed = true;
    }
    if (!renewed) {
      recs.push({
        ...base,
        priority: marbete.status === 'overdue' || marbete.daysLeft <= 30 ? 'high' : 'medium',
        type: 'marbete',
        marbeteStatus: marbete.status,
        daysLeft: marbete.daysLeft,
        daysOver: marbete.daysOver,
      });
    }
  }

  // 5. Documentos con fecha registrada por el dueño: inspección técnica (revista),
  // seguro y licencia. A diferencia del marbete (ciclo nacional fijo), vencen por
  // vehículo, así que solo se recuerdan a partir de la fecha que el dueño registró.
  ['inspeccion', 'seguro', 'licencia'].forEach(docKey => {
    const raw = vehicle.documents?.[docKey]?.expiresAt;
    if (!raw) return;
    const exp = typeof raw?.toDate === 'function'
      ? raw.toDate()
      : (raw instanceof Date ? raw : new Date(raw + 'T00:00:00'));
    if (isNaN(exp.getTime())) return;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const daysLeft = Math.floor((exp - today) / 86400000);
    if (daysLeft > 30) return;
    recs.push({
      ...base,
      priority: daysLeft < 0 ? 'high' : 'medium',
      type: 'document',
      docKey,
      status: daysLeft < 0 ? 'overdue' : 'soon',
      daysLeft,
      daysOver: daysLeft < 0 ? -daysLeft : 0,
    });
  });

  return recs;
}